//! In-editor undo/redo history for the cell text editor (shared by the in-cell
//! editor and the formula bar). The editor surface is a controlled <textarea>,
//! whose native undo stack only tracks text — it cannot restore rich-text
//! formatting (links/bold/italic/color), which lives in `inlineEditorSpans`
//! separately from the text. This controller captures the full editor state
//! (text + spans + selection) as snapshots so a single undo restores both.
//!
//! While a rich-text editing session is active, the editors route undo/redo here
//! and the browser's native textarea undo is suppressed, keeping one coherent
//! history. Grid/core undo is untouched and continues to handle committed cells.

import { inlineEditorSpans, type TrackedSpan } from '@/app/gridGL/HTMLGrid/inlineEditor/inlineEditorSpans';

export interface EditorSnapshot {
  value: string;
  spans: TrackedSpan[];
  selectionStart: number;
  selectionEnd: number;
}

// Restores a snapshot into the active editor's React state. Registered by
// whichever editor owns the current session (only one edits at a time).
type ApplyFn = (snapshot: EditorSnapshot) => void;

const MAX_HISTORY = 200;

// Consecutive typing within this window collapses into a single undo step, so
// undo reverts word/burst-sized chunks rather than one character at a time.
const TYPING_COALESCE_MS = 500;

const cloneSpans = (spans: TrackedSpan[]): TrackedSpan[] => spans.map((span) => ({ ...span }));

class InlineEditorHistory {
  private active = false;
  private restoring = false;
  // True while a multi-step operation is grouped into a single undo entry.
  private batching = false;
  // Whether a text/format commit happened during the current batch. When true,
  // endBatch keeps that commit's value/selection (the textarea DOM lags React
  // state right after a programmatic edit) and only refreshes spans.
  private batchHadCommit = false;

  // The current (live) editor state. Pushed onto the undo stack before each new
  // change so it can be returned to.
  private present: EditorSnapshot | undefined;
  private undoStack: EditorSnapshot[] = [];
  private redoStack: EditorSnapshot[] = [];
  private applyFn: ApplyFn | undefined;

  // Typing-burst coalescing state. A burst groups consecutive single-character
  // edits of the same direction into one undo step; it breaks on whitespace
  // (word boundaries), on switching between typing and deleting, on
  // multi-character/programmatic edits, and after an idle pause.
  private lastWasTyping = false;
  private lastTypingTime = 0;
  private lastDirection: 'insert' | 'delete' | 'none' = 'none';
  // When true, the next typed edit starts a fresh undo group (set after a
  // whitespace char is inserted/deleted, ending the current word).
  private breakNextEdit = false;

  get isActive(): boolean {
    return this.active;
  }

  // True while a snapshot is being restored; mutation chokepoints check this to
  // avoid recording the restore itself as a new history entry.
  get isRestoring(): boolean {
    return this.restoring;
  }

  // Begin a new editing session with the given initial state and apply callback.
  reset(initial: EditorSnapshot, applyFn: ApplyFn) {
    this.active = true;
    this.restoring = false;
    this.batching = false;
    this.batchHadCommit = false;
    this.present = { ...initial, spans: cloneSpans(initial.spans) };
    this.undoStack = [];
    this.redoStack = [];
    this.applyFn = applyFn;
    this.resetBurst();
    this.lastTypingTime = 0;
  }

  // Re-baseline to the current live state and drop history. Used after the
  // cell's spans finish loading asynchronously (formula bar), so the loaded
  // content becomes the session's starting point rather than an undoable step.
  rebaseline() {
    if (!this.active || this.restoring) return;
    this.present = this.capture();
    this.undoStack = [];
    this.redoStack = [];
    this.resetBurst();
  }

  clear() {
    this.active = false;
    this.restoring = false;
    this.batching = false;
    this.batchHadCommit = false;
    this.present = undefined;
    this.undoStack = [];
    this.redoStack = [];
    this.applyFn = undefined;
    this.resetBurst();
  }

  // Record a text change (typing or programmatic). Consecutive typing coalesces
  // into one undo step.
  commitText(value: string, caret: number) {
    if (!this.active || this.restoring) return;
    if (this.batching) {
      this.batchHadCommit = true;
      this.present = {
        value,
        spans: cloneSpans(inlineEditorSpans.getSpans()),
        selectionStart: caret,
        selectionEnd: caret,
      };
      return;
    }

    const now = Date.now();
    const prev = this.present?.value ?? '';
    const delta = value.length - prev.length;
    // Only single-character insert/delete edits coalesce; anything else
    // (paste, autocomplete, multi-char programmatic edits) is its own step.
    const direction: 'insert' | 'delete' | 'none' = delta === 1 ? 'insert' : delta === -1 ? 'delete' : 'none';

    let startNewGroup = false;
    if (!this.lastWasTyping) {
      startNewGroup = true;
    } else if (now - this.lastTypingTime >= TYPING_COALESCE_MS) {
      startNewGroup = true;
    } else if (direction === 'none' || direction !== this.lastDirection) {
      startNewGroup = true;
    } else if (this.breakNextEdit) {
      startNewGroup = true;
    }

    if (startNewGroup) this.pushPresent();

    // A whitespace insert/delete ends the current word, so the next edit starts
    // a new undo group.
    let boundaryChar = '';
    if (direction === 'insert') boundaryChar = value[caret - 1] ?? '';
    else if (direction === 'delete') boundaryChar = prev[caret] ?? '';
    this.breakNextEdit = direction !== 'none' && /\s/.test(boundaryChar);

    this.lastWasTyping = direction !== 'none';
    this.lastDirection = direction;
    this.lastTypingTime = now;

    this.present = {
      value,
      spans: cloneSpans(inlineEditorSpans.getSpans()),
      selectionStart: caret,
      selectionEnd: caret,
    };
  }

  // Record a discrete change (formatting/link toggle). Never coalesces.
  commit() {
    if (!this.active || this.restoring) return;
    if (this.batching) {
      this.batchHadCommit = true;
    } else {
      this.pushPresent();
      this.resetBurst();
    }
    this.present = this.capture();
  }

  // Group the changes between begin/end into a single undo step (e.g. a link
  // edit that replaces text and updates a span at once).
  beginBatch() {
    if (!this.active || this.restoring || this.batching) return;
    this.pushPresent();
    this.batching = true;
    this.batchHadCommit = false;
    this.resetBurst();
  }

  endBatch() {
    if (!this.active || this.restoring || !this.batching) return;
    this.batching = false;
    if (this.batchHadCommit && this.present) {
      // A text/format commit during the batch already set the authoritative
      // value/selection; just refresh spans to their final state.
      this.present = { ...this.present, spans: cloneSpans(inlineEditorSpans.getSpans()) };
    } else {
      // Pure span change (no text edit) — the DOM value is current, so capture.
      this.present = this.capture();
    }
    this.batchHadCommit = false;
  }

  undo(): boolean {
    if (!this.active || this.undoStack.length === 0 || !this.present) return false;
    this.redoStack.push(this.present);
    const snapshot = this.undoStack.pop()!;
    this.present = snapshot;
    this.resetBurst();
    this.applySnapshot(snapshot);
    return true;
  }

  redo(): boolean {
    if (!this.active || this.redoStack.length === 0 || !this.present) return false;
    this.undoStack.push(this.present);
    const snapshot = this.redoStack.pop()!;
    this.present = snapshot;
    this.resetBurst();
    this.applySnapshot(snapshot);
    return true;
  }

  // End the current typing burst so the next text edit starts a fresh group.
  private resetBurst() {
    this.lastWasTyping = false;
    this.lastDirection = 'none';
    this.breakNextEdit = false;
  }

  private pushPresent() {
    if (!this.present) return;
    this.undoStack.push(this.present);
    if (this.undoStack.length > MAX_HISTORY) this.undoStack.shift();
    this.redoStack = [];
  }

  private capture(): EditorSnapshot {
    const surface = inlineEditorSpans.getActiveSurface();
    const value = surface.get();
    const selection = surface.getSelection();
    const caret = surface.getCaret();
    return {
      value,
      spans: cloneSpans(inlineEditorSpans.getSpans()),
      selectionStart: selection?.start ?? caret,
      selectionEnd: selection?.end ?? caret,
    };
  }

  private applySnapshot(snapshot: EditorSnapshot) {
    this.restoring = true;
    try {
      inlineEditorSpans.restoreSpans(snapshot.spans);
      this.applyFn?.({ ...snapshot, spans: cloneSpans(snapshot.spans) });
    } finally {
      this.restoring = false;
    }
  }
}

export const inlineEditorHistory = new InlineEditorHistory();
